/** The Type Set engine seam. Exact browser files are production UI evidence;
 * editable-deck compatibility remains a separate, visible human test. */

import {
  buildDirectionCard,
  splitSharedBrief,
  type DirectionCard,
  type PreviewCopy,
  type SharedBrief,
  type TypeCatalog,
  type TypeRecommendation,
} from "@core/index.js";

import { profiles, samplePacks, typeCatalog } from "./catalog";
import { createExactTypeStudio, legacyTypeForSession } from "./typeLibrary";

export const TYPE_MODE = "production" as const;

/** Exact self-hosted recommendation. No network font request is required. */
export const runType = (brief: SharedBrief): TypeRecommendation | null => {
  const session = createExactTypeStudio(brief);
  return session ? legacyTypeForSession(session) : null;
};

export const typeAnswersFor = (brief: SharedBrief) => splitSharedBrief({ ...brief, route: "type" }).type;

/** Use the user's words when present. Otherwise choose an invented, labelled
 * fixture by deck job—never client copy, never lorem ipsum. */
export const previewCopyFor = (brief: SharedBrief): { copy: PreviewCopy; fixture: string | null } => {
  const own = brief.previewCopy ?? {};
  const hasOwn = Object.values(own).some((value) => typeof value === "string" && value.trim().length > 0);
  if (hasOwn) return { copy: own, fixture: null };
  const packIdByArtifact: Record<string, string> = {
    filmTvProject: "film",
    directorTreatment: "film",
    advertisingTreatment: "film",
    startupInvestor: "venture",
    agencyPitch: "project",
    companyCredentials: "project",
    salesPartnership: "venture",
    other: "plain",
  };
  const packId = packIdByArtifact[brief.artifactType ?? "other"] ?? "plain";
  const pack = samplePacks.find((item) => item.id === packId) ?? samplePacks.find((item) => item.id === "plain");
  const fields = pack?.fields ?? {};
  return {
    copy: {
      deckTitle: fields.headline,
      subtitle: fields.subheadline,
      slideTitle: fields.headline,
      body: fields.body,
      quote: fields.quote,
      attribution: fields.caption,
      metric: fields.dataValue,
      metricLabel: fields.dataLabel,
    },
    fixture: pack ? `${pack.name} — invented fixture copy` : null,
  };
};

export const composeDirection = (input: {
  projectId: string;
  name?: string;
  type: TypeRecommendation;
  previewCopy?: PreviewCopy;
  selectedTypeSystemId?: string | null;
  typeCatalogOverride?: TypeCatalog;
}): DirectionCard => {
  let type = input.type;
  if (input.selectedTypeSystemId && type.recommendations.some((item) => item.systemId === input.selectedTypeSystemId)) {
    type = {
      ...type,
      recommendations: [
        ...type.recommendations.filter((item) => item.systemId === input.selectedTypeSystemId),
        ...type.recommendations.filter((item) => item.systemId !== input.selectedTypeSystemId),
      ],
    };
  }
  return buildDirectionCard({
    projectId: input.projectId,
    ...(input.name !== undefined ? { name: input.name } : {}),
    type,
    typeCatalog: input.typeCatalogOverride ?? typeCatalog,
    ...(input.previewCopy !== undefined ? { previewCopy: input.previewCopy } : {}),
  });
};

/** Compatibility seam for opening an older combined project. */
export const isUnrunType = (type: TypeRecommendation): boolean =>
  type.recommendations.length === 0 && type.outcome === "unsupported";

export const artifactLabel = (artifactType: string | undefined): string =>
  profiles.find((profile) => profile.id === artifactType)?.label ?? "a presentation";
